import { Button, InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from '@racefront/shadcn-ui'
import { useEffect, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

interface VerificationModalProps {
  isOpen: boolean
  onClose: () => void
  verificationType: 'email' | 'phone_number'
  preview?: string
  onConfirm: (code: string) => void
  isLoading?: boolean
  error?: string
}

export function VerificationModal({
  isOpen,
  onClose,
  verificationType,
  preview,
  onConfirm,
  isLoading,
}: VerificationModalProps) {
  const intl = useIntl()
  const [code, setCode] = useState('')

  // Clear any code left over from a previous verification whenever the modal opens.
  useEffect(() => {
    if (isOpen) setCode('')
  }, [isOpen])

  const handleConfirm = () => {
    if (code.length === 6) {
      onConfirm(code)
    }
  }

  if (!isOpen) return null

  const medium =
    verificationType === 'email'
      ? intl.formatMessage({ id: 'account.verification.medium.email', defaultMessage: 'email' })
      : intl.formatMessage({ id: 'account.verification.medium.phone', defaultMessage: 'phone number' })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-background mx-4 w-full max-w-md rounded-lg p-6 shadow-lg">
        <h2 className="mb-2 text-lg font-semibold">
          <FormattedMessage
            id="account.verification.title"
            defaultMessage="Verify {medium} Change"
            values={{ medium }}
          />
        </h2>
        <p className="text-muted-foreground mb-4">
          <FormattedMessage
            id="account.verification.description"
            defaultMessage="We have just sent a verification code to your new {medium}. Fill in the code below to confirm the change. If you abort verification your previous {medium} will remain active."
            values={{
              preview: preview || '',
              medium,
            }}
          />
        </p>

        <div className="mb-8 mt-8 flex justify-center">
          <InputOTP maxLength={6} onChange={setCode} value={code}>
            <InputOTPGroup>
              <InputOTPSlot index={0} />
              <InputOTPSlot index={1} />
              <InputOTPSlot index={2} />
            </InputOTPGroup>
            <InputOTPSeparator />
            <InputOTPGroup>
              <InputOTPSlot index={3} />
              <InputOTPSlot index={4} />
              <InputOTPSlot index={5} />
            </InputOTPGroup>
          </InputOTP>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
          </Button>
          <Button onClick={handleConfirm} disabled={code?.length !== 6 || isLoading}>
            <FormattedMessage id="account.verification.confirm" defaultMessage="Confirm" />
          </Button>
        </div>
      </div>
    </div>
  )
}
